<template>
  <MarketingPageTemplate
    :title="data.title || 'Digitale Kampagnen'"
    :subtitle="data.subtitle || 'Auslieferung und Performance nach Website und Schaltungstyp'"
    :loading="loading"
    :loaded-once="loadedOnce"
    :error="error"
    :legend="data.legend || []"
    loading-text="Lade digitale Kampagnen…"
    @retry="reload"
  >
    <template #channel-filters>
      <div class="fpair">
        <span class="flabel">Website</span>
        <SingleDropdown
          label="Website"
          :options="websiteOptions"
          :model-value="websiteId"
          @update:model-value="(value) => setExtra({ website_id: value })"
        />
      </div>
    </template>

    <template #kpis>
      <MarketingKpis :cards="data.kpis || []" :columns="3" />
    </template>

    <!-- Wie bisher: ein gleich aufgebauter Block je Schaltungstyp (Feedback: "dieselben Diagramme und
         Kennzahlen mit derselben Struktur") – Kennzahlen · Verlauf · Performance nach Website. -->
    <DashboardSection
      v-for="block in placementBlocks"
      :key="block.code"
      :title="block.name"
      :subtitle="block.legend"
    >
      <template #title-prefix>
        <PlacementTypeIcon :code="block.code" />
      </template>
      <MarketingKpis :cards="typeKpis(block)" :columns="3" />
      <!-- UX: Höchstens 2 Y-Achsen (Impressions links, Klicks rechts); die CTR steht als Kennzahl und in der Tabelle. -->
      <MarketingChart
        :categories="typeDates(block).map((row) => row.date)"
        :series="[
          { name: 'Gelieferte Impressions', data: typeDates(block).map((row) => row.impressions) },
          { name: 'Klicks', data: typeDates(block).map((row) => row.clicks), axis: 'right' }
        ]"
        :colors="[placementShades(block.code).light, placementShades(block.code).dark]"
        datetime
        x-title="Datum"
      />
      <h3 class="mb-subtitle">Performance nach Website</h3>
      <p v-if="!(block.websites || []).length" class="jr-empty">Keine Websites für diesen Schaltungstyp.</p>
      <TableScroll v-else>
        <table class="jr-list">
          <thead>
            <tr>
              <th scope="col">Ausspielungsort</th>
              <th scope="col" class="num">Gelieferte Impressions</th>
              <th scope="col" class="num">Klicks</th>
              <th scope="col" class="num">CTR</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in block.websites" :key="row.website_id">
              <td :title="row.name">{{ row.name }}</td>
              <td class="num">{{ formatNumber(row.impressions) }}</td>
              <td class="num">{{ formatNumber(row.clicks) }}</td>
              <!-- UX: CTR mit 2 Nachkommastellen (0,05 % statt 0 %). -->
              <td class="num">{{ formatRate(row.ctr, row.clicks, row.impressions) }}</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
    </DashboardSection>

    <template #legend>
      <PlacementTypeLegend :items="placementLegend" />
    </template>
  </MarketingPageTemplate>
</template>

<script setup>
import { computed } from 'vue';
import SingleDropdown from '@/features/dashboard/components/SingleDropdown.vue';
import MarketingPageTemplate from '../components/MarketingPageTemplate.vue';
import DashboardSection from '../components/DashboardSection.vue';
import MarketingKpis from '../components/MarketingKpis.vue';
import MarketingChart from '../components/MarketingChart.vue';
import PlacementTypeIcon from '../components/PlacementTypeIcon.vue';
import PlacementTypeLegend from '../components/PlacementTypeLegend.vue';
import TableScroll from '../components/TableScroll.vue';
import { useMarketingDashboard } from '../composables/useMarketingDashboard';
import { useMarketingFilters } from '../composables/useMarketingFilters';
import { placementShades } from '../composables/designTokens';
import { formatNumber, formatRate } from '../composables/formatters';

const props = defineProps({
  filterOptions: { type: Object, default: () => ({}) }
});

const { extraValue, setExtra } = useMarketingFilters();
const websiteId = computed(() => extraValue('website_id', ''));
const extraParams = computed(() => ({
  website_id: websiteId.value
}));
const { data, loading, error, loadedOnce, reload } = useMarketingDashboard('/mb/digital/', extraParams);

const websiteOptions = computed(() => [
  { value: '', label: 'Alle Websites' },
  ...(props.filterOptions.websites || []).map((item) => ({
    value: String(item.id),
    label: item.short_name || item.name
  }))
]);

const placementBlocks = computed(() => data.value.by_placement_type || []);
const placementLegend = computed(() => (
  props.filterOptions.placement_types?.length
    ? props.filterOptions.placement_types
    : placementBlocks.value
));

function typeKpis(block) {
  return [
    { key: 'impressions', label: 'Gelieferte Impressions', value: block.impressions, format: 'number' },
    { key: 'clicks', label: 'Klicks', value: block.clicks, format: 'number' },
    { key: 'ctr', label: 'CTR', value: block.ctr, numerator: block.clicks, denominator: block.impressions, format: 'percent' }
  ];
}

function typeDates(block) {
  return data.value.series?.by_placement_type?.[block.code]?.dates || [];
}
</script>
